import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Heart, ChatCircle, Plus, Trash, UploadSimple, InstagramLogo, UserCircle, PaperPlaneRight } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("es-MX");
}

function roleColor(r) {
  if (r === "admin") return "text-[#CCFF00]";
  if (r === "directivo") return "text-blue-400";
  if (r === "profesor") return "text-yellow-400";
  return "text-gray-400";
}

function shareToInstagram(post) {
  const text = encodeURIComponent(`${post.caption || ""} · ${post.club_name || ""}`);
  // Instagram doesn't allow direct URL share; copy text + open IG
  const igApp = `instagram://library?LocalIdentifier=&caption=${text}`;
  const igWeb = "https://www.instagram.com/";
  try {
    navigator.clipboard.writeText(post.caption || "");
    toast.success("Descripcion copiada. Abriendo Instagram...");
  } catch { /* ignore */ }
  const win = window.open(igApp, "_blank");
  setTimeout(() => {
    if (win) win.location = igWeb;
    else window.open(igWeb, "_blank");
  }, 500);
}

export default function ClubFeed({ clubId, clubName, instagramHandle }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentsByPost, setCommentsByPost] = useState({});
  const [newComment, setNewComment] = useState({});
  const [replyTo, setReplyTo] = useState({});
  const fileRef = useRef(null);

  const loadPosts = async () => {
    try {
      const { data } = await api.get(`/clubs/${clubId}/posts`);
      setPosts(data);
    } catch {
      toast.error("Error al cargar publicaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Max 50MB");
      return;
    }
    setUploadFile(f);
    setUploadOpen(true);
  };

  const submitUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("caption", uploadCaption);
    try {
      await api.post(`/clubs/${clubId}/posts`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Publicacion creada");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadCaption("");
      if (fileRef.current) fileRef.current.value = "";
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al publicar");
    } finally {
      setUploading(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Eliminar publicacion?")) return;
    try {
      await api.delete(`/posts/${postId}`);
      toast.success("Eliminada");
      setPosts((p) => p.filter((x) => x.id !== postId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const toggleLike = async (post) => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setPosts((arr) => arr.map((p) => {
        if (p.id !== post.id) return p;
        const newLikes = data.liked
          ? [...(p.likes || []), user.id]
          : (p.likes || []).filter((u) => u !== user.id);
        return { ...p, likes: newLikes };
      }));
    } catch {
      toast.error("Error");
    }
  };

  const loadComments = async (postId) => {
    try {
      const { data } = await api.get(`/posts/${postId}/comments`);
      setCommentsByPost((m) => ({ ...m, [postId]: data }));
    } catch {
      toast.error("Error al cargar comentarios");
    }
  };

  const toggleComments = async (postId) => {
    const wasOpen = expandedComments[postId];
    setExpandedComments((m) => ({ ...m, [postId]: !wasOpen }));
    if (!wasOpen && !commentsByPost[postId]) {
      await loadComments(postId);
    }
  };

  const submitComment = async (postId) => {
    const text = (newComment[postId] || "").trim();
    if (!text) return;
    const parent_comment_id = replyTo[postId] || null;
    try {
      await api.post(`/posts/${postId}/comments`, { text, parent_comment_id });
      setNewComment((m) => ({ ...m, [postId]: "" }));
      setReplyTo((m) => ({ ...m, [postId]: null }));
      setPosts((arr) => arr.map((p) => p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
      await loadComments(postId);
    } catch {
      toast.error("Error al comentar");
    }
  };

  const likeComment = async (postId, commentId) => {
    try {
      const { data } = await api.post(`/comments/${commentId}/like`);
      setCommentsByPost((m) => ({
        ...m,
        [postId]: m[postId].map((c) => {
          if (c.id !== commentId) return c;
          const newLikes = data.liked ? [...(c.likes || []), user.id] : (c.likes || []).filter((u) => u !== user.id);
          return { ...c, likes: newLikes };
        }),
      }));
    } catch (err) { console.error("Like comment error:", err); }
  };

  const deleteComment = async (postId, commentId) => {
    if (!window.confirm("Eliminar comentario?")) return;
    try {
      await api.delete(`/comments/${commentId}`);
      setCommentsByPost((m) => ({ ...m, [postId]: m[postId].filter((c) => c.id !== commentId) }));
      setPosts((arr) => arr.map((p) => p.id === postId ? { ...p, comment_count: Math.max(0, (p.comment_count || 0) - 1) } : p));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  // Build threaded comment tree
  const buildTree = (comments) => {
    const byParent = {};
    comments.forEach((c) => {
      const key = c.parent_comment_id || "root";
      byParent[key] = byParent[key] || [];
      byParent[key].push(c);
    });
    return byParent;
  };

  return (
    <div className="space-y-5" data-testid="club-feed">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Comunidad</p>
          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white">Album y Feed</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {instagramHandle && (
            <a href={`https://instagram.com/${instagramHandle.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" data-testid="club-instagram-link">
              <Button size="sm" variant="outline" className="border-pink-500/40 text-pink-400 hover:bg-pink-500/10 text-xs uppercase font-bold">
                <InstagramLogo size={14} weight="fill" className="mr-1" /> @{instagramHandle.replace(/^@/, "")}
              </Button>
            </a>
          )}
          <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleFile} data-testid="post-file-input" />
          <Button size="sm" onClick={() => fileRef.current?.click()} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="new-post-btn">
            <Plus size={14} className="mr-1" /> Publicar
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="card-tactical p-12 text-center">
          <ChatCircle size={48} weight="duotone" className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Aun no hay publicaciones en este club.</p>
          <p className="text-xs text-gray-600 mt-2">Se el primero en compartir una foto o video.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {posts.map((p) => {
            const liked = p.likes?.includes(user?.id);
            const canDelete = p.author_id === user?.id || user?.role === "admin" || (user?.role === "directivo" && user?.club_id === clubId);
            const comments = commentsByPost[p.id] || [];
            const tree = buildTree(comments);
            const roots = tree.root || [];
            const mediaUrl = `${BACKEND_URL}/api/posts/${p.id}/media`;
            return (
              <article key={p.id} className="card-tactical overflow-hidden" data-testid={`post-${p.id}`}>
                {/* header */}
                <header className="flex items-center gap-3 p-4">
                  {p.author_photo ? (
                    <img src={p.author_photo} alt={p.author_name} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                  ) : (
                    <UserCircle size={40} weight="duotone" className="text-gray-600 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold text-sm ${roleColor(p.author_role)}`}>{p.author_name}</span>
                      {p.author_role && p.author_role !== "jugador" && (
                        <span className="text-[9px] uppercase tracking-wider text-gray-500">· {p.author_role}</span>
                      )}
                    </div>
                    <span className="text-[11px] text-gray-500 font-['JetBrains_Mono']">{timeAgo(p.created_at)}</span>
                  </div>
                  {canDelete && (
                    <Button variant="ghost" size="icon" onClick={() => deletePost(p.id)} className="text-gray-500 hover:text-red-400 h-8 w-8" data-testid={`delete-post-${p.id}`}>
                      <Trash size={16} />
                    </Button>
                  )}
                </header>

                {/* media */}
                <div className="bg-[#0A0A0A] flex items-center justify-center max-h-[600px] overflow-hidden">
                  {p.media_type === "video" ? (
                    <video src={mediaUrl} controls className="max-w-full max-h-[600px]" data-testid={`post-video-${p.id}`} />
                  ) : (
                    <img src={mediaUrl} alt={p.caption || "Post"} className="max-w-full max-h-[600px] object-contain" data-testid={`post-image-${p.id}`} />
                  )}
                </div>

                {/* actions */}
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-4">
                    <button onClick={() => toggleLike(p)} className={`flex items-center gap-1 text-sm transition-colors ${liked ? "text-pink-500" : "text-gray-400 hover:text-pink-400"}`} data-testid={`like-post-${p.id}`}>
                      <Heart size={20} weight={liked ? "fill" : "regular"} />
                      <span className="font-['JetBrains_Mono'] text-xs">{p.likes?.length || 0}</span>
                    </button>
                    <button onClick={() => toggleComments(p.id)} className="flex items-center gap-1 text-sm text-gray-400 hover:text-[#CCFF00]" data-testid={`toggle-comments-${p.id}`}>
                      <ChatCircle size={20} />
                      <span className="font-['JetBrains_Mono'] text-xs">{p.comment_count || 0}</span>
                    </button>
                    <button onClick={() => shareToInstagram(p)} className="flex items-center gap-1 text-sm text-gray-400 hover:text-pink-400 ml-auto" data-testid={`share-ig-${p.id}`} title="Compartir en Instagram">
                      <InstagramLogo size={20} />
                      <span className="text-[10px] uppercase tracking-wider">Share</span>
                    </button>
                  </div>
                  {p.caption && <p className="text-sm text-gray-200 whitespace-pre-wrap"><span className={`font-semibold ${roleColor(p.author_role)}`}>{p.author_name}</span> {p.caption}</p>}

                  {/* comments */}
                  {expandedComments[p.id] && (
                    <div className="mt-3 pt-3 border-t border-white/10 space-y-3" data-testid={`comments-${p.id}`}>
                      {roots.length === 0 ? (
                        <p className="text-xs text-gray-500 text-center">Sin comentarios. Se el primero.</p>
                      ) : (
                        roots.map((c) => (
                          <CommentItem
                            key={c.id}
                            comment={c}
                            postId={p.id}
                            clubId={clubId}
                            tree={tree}
                            user={user}
                            onLike={likeComment}
                            onDelete={deleteComment}
                            onReply={(cid) => { setReplyTo((m) => ({ ...m, [p.id]: cid })); }}
                          />
                        ))
                      )}
                      {replyTo[p.id] && (
                        <div className="flex items-center justify-between bg-[#0A0A0A] border border-[#CCFF00]/20 px-3 py-1.5 text-xs">
                          <span className="text-gray-400">Respondiendo a comentario</span>
                          <button onClick={() => setReplyTo((m) => ({ ...m, [p.id]: null }))} className="text-gray-500 hover:text-red-400">Cancelar</button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Agrega un comentario..."
                          value={newComment[p.id] || ""}
                          onChange={(e) => setNewComment((m) => ({ ...m, [p.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitComment(p.id); } }}
                          className="bg-[#0A0A0A] border-white/20 text-white h-9 text-sm focus:border-[#CCFF00]"
                          data-testid={`comment-input-${p.id}`}
                        />
                        <Button size="sm" onClick={() => submitComment(p.id)} disabled={!(newComment[p.id] || "").trim()} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold text-xs disabled:opacity-40" data-testid={`comment-submit-${p.id}`}>
                          <PaperPlaneRight size={14} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Nueva Publicacion</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {uploadFile && (
              <div className="bg-[#0A0A0A] border border-white/10 p-3 text-xs">
                <p className="text-gray-400">{uploadFile.name}</p>
                <p className="text-[10px] text-gray-600 font-['JetBrains_Mono']">{(uploadFile.size / 1024 / 1024).toFixed(2)} MB · {uploadFile.type}</p>
              </div>
            )}
            <Textarea
              placeholder="Escribe una descripcion..."
              value={uploadCaption}
              onChange={(e) => setUploadCaption(e.target.value)}
              rows={4}
              maxLength={2000}
              className="bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] resize-none"
              data-testid="upload-caption"
            />
            <p className="text-[10px] text-gray-600 text-right font-['JetBrains_Mono']">{uploadCaption.length}/2000</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUploadOpen(false)} disabled={uploading} className="text-gray-400 text-xs uppercase font-bold">Cancelar</Button>
            <Button onClick={submitUpload} disabled={uploading} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="upload-submit">
              <UploadSimple size={14} className="mr-1" /> {uploading ? "Subiendo..." : "Publicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CommentItem({ comment, postId, tree, user, onLike, onDelete, onReply, depth = 0, clubId }) {
  const liked = comment.likes?.includes(user?.id);
  const canDelete = comment.author_id === user?.id || user?.role === "admin" || (user?.role === "directivo" && user?.club_id === clubId);
  const children = tree[comment.id] || [];
  return (
    <div className={depth > 0 ? "ml-6 border-l border-white/5 pl-3" : ""} data-testid={`comment-${comment.id}`}>
      <div className="flex items-start gap-2">
        {comment.author_photo ? (
          <img src={comment.author_photo} alt={comment.author_name} className="w-7 h-7 rounded-full object-cover border border-white/10" />
        ) : (
          <UserCircle size={28} weight="duotone" className="text-gray-600 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="bg-[#0A0A0A] border border-white/10 px-3 py-2">
            <span className={`text-xs font-semibold ${roleColor(comment.author_role)}`}>{comment.author_name}</span>
            <p className="text-sm text-gray-200 whitespace-pre-wrap break-words">{comment.text}</p>
          </div>
          <div className="flex items-center gap-3 mt-1 pl-1 text-[11px] text-gray-500">
            <span className="font-['JetBrains_Mono']">{timeAgo(comment.created_at)}</span>
            <button onClick={() => onLike(postId, comment.id)} className={`flex items-center gap-0.5 hover:text-pink-400 ${liked ? "text-pink-500" : ""}`}>
              <Heart size={11} weight={liked ? "fill" : "regular"} /> {comment.likes?.length || 0}
            </button>
            {depth === 0 && <button onClick={() => onReply(comment.id)} className="hover:text-[#CCFF00]">Responder</button>}
            {canDelete && <button onClick={() => onDelete(postId, comment.id)} className="hover:text-red-400">Eliminar</button>}
          </div>
        </div>
      </div>
      {depth === 0 && children.length > 0 && (
        <div className="mt-2 space-y-2">
          {children.map((c) => {
            const cLiked = c.likes?.includes(user?.id);
            const cCanDelete = c.author_id === user?.id || user?.role === "admin" || (user?.role === "directivo" && user?.club_id === clubId);
            return (
              <div key={c.id} className="ml-6 border-l border-white/5 pl-3" data-testid={`comment-${c.id}`}>
                <div className="flex items-start gap-2">
                  {c.author_photo ? (
                    <img src={c.author_photo} alt={c.author_name} className="w-6 h-6 rounded-full object-cover border border-white/10" />
                  ) : (
                    <UserCircle size={24} weight="duotone" className="text-gray-600 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="bg-[#0A0A0A] border border-white/10 px-3 py-1.5">
                      <span className={`text-xs font-semibold ${roleColor(c.author_role)}`}>{c.author_name}</span>
                      <p className="text-sm text-gray-200 whitespace-pre-wrap break-words">{c.text}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-1 pl-1 text-[11px] text-gray-500">
                      <span className="font-['JetBrains_Mono']">{timeAgo(c.created_at)}</span>
                      <button onClick={() => onLike(postId, c.id)} className={`flex items-center gap-0.5 hover:text-pink-400 ${cLiked ? "text-pink-500" : ""}`}>
                        <Heart size={11} weight={cLiked ? "fill" : "regular"} /> {c.likes?.length || 0}
                      </button>
                      {cCanDelete && <button onClick={() => onDelete(postId, c.id)} className="hover:text-red-400">Eliminar</button>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
